import { Candidate, CandidateProfile } from './candidate.model';
import { Tag } from './tag.model';
import { TalentPoolResult } from './talent-pool.model';

export interface CandidateSearchRequest {
  clientId: string;
  searchTerm: string;
  page: number;
  pageSize: number;
  filters?: CandidateSearchFilters;
}

export interface CandidateSearchFilters {
  jobType?: number;
  availability?: CandidateSearchAvailabilityFilter;
}

export interface CandidateSearchProfileViewContext {
  clientId: string;
  searchTerm: string;
  resultCount: number;
  filters?: CandidateSearchFilters;
}

export interface CandidateSearchAvailabilityFilter {
  dates: string[];
  matchMode: CandidateAvailabilityMatchMode;
}

export enum CandidateAvailabilityMatchMode {
  AnyAvailable = 1,
  AllAvailable = 2
}

export interface ExploreTalentSearchParameters {
  searchTerm?: string;
  page: number;
  pageSize: number;
  jobType?: number;
  availabilityDates?: string[];
}

export interface CandidateSearchResponse {
  items: CandidateSearchResultItem[];
  totalCount: number;
  page: number;
  pageSize: number;
  hasMore: boolean;
}

export interface CandidateSearchSuggestionsRequest {
  clientId: string;
  searchTerm: string;
  limit: number;
}

export interface CandidateSearchSuggestionsResponse {
  items: CandidateSearchSuggestion[];
  totalCount: number;
  hasMore: boolean;
}

export interface CandidateSearchSuggestion {
  candidateId: string;
  firstName?: string;
  goesBy?: string;
  lastName?: string;
  hasWorkedForClient: boolean;
  imageUrl?: string | null;
  defaultJobRole?: string;
  distanceKm?: number;
  isBankStaff: boolean;
}

export interface CandidateSearchTalentPoolResult extends TalentPoolResult {
  TotalCount: number;
  Page: number;
  PageSize: number;
  HasMore: boolean;
}

export interface CandidateSearchResultItem {
  candidateId: string;
  name: CandidateSearchName;
  media: CandidateSearchMedia;
  location: CandidateSearchLocation;
  profiles: CandidateSearchProfile[];
  tags: CandidateSearchTag[];
  metrics: CandidateSearchMetrics;
  flags: CandidateSearchFlags;
  relevanceScore: number;
}

export interface CandidateSearchName {
  firstName?: string;
  goesBy?: string;
  lastName?: string;
  hasWorkedForClient: boolean;
}

export interface CandidateSearchMedia {
  imageUrl?: string | null;
}

export interface CandidateSearchLocation {
  area?: string;
  distanceKm?: number;
}

export interface CandidateSearchProfile {
  candidateCardId: number;
  roleId: string;
  roleName: string;
  roleGroupId?: number;
  isPrimary: boolean;
  dailySupply: boolean;
  longTerm: boolean;
  summary?: string;
  dayPayRate?: number;
  halfDayPayRate?: number;
  longTermDayPayRate?: number;
  hourlyRate?: number;
}

export interface CandidateSearchTag {
  id: string;
  name: string;
}

export interface CandidateSearchMetrics {
  consultantScore?: number;
  completedJobs?: number;
  completedJobsAtClient?: number;
  defaultSummary?: string;
}

export interface CandidateSearchFlags {
  isBankStaff: boolean;
  isInTalentPool: boolean;
}

export function mapCandidateSearchResponseToTalentPoolResult(response: CandidateSearchResponse): CandidateSearchTalentPoolResult {
  const totalCount = response?.totalCount ?? 0;

  return {
    ClientHasFavourites: false,
    RoleGroupSets: [{
      RoleGroupId: 0,
      FavouritesFiltered: 0,
      TotalFiltered: totalCount,
      RoleGroupOrder: 0
    }],
    Candidates: (response?.items ?? []).map(mapCandidateSearchItemToCandidate),
    TotalCount: totalCount,
    Page: response?.page ?? 1,
    PageSize: response?.pageSize ?? 0,
    HasMore: response?.hasMore ?? false
  };
}

function mapCandidateSearchItemToCandidate(item: CandidateSearchResultItem): Candidate {
  const displayName = getCandidateSearchDisplayName(item.name);
  const firstName = item.name?.goesBy || item.name?.firstName || '';
  const lastName = item.name?.hasWorkedForClient ? item.name?.lastName || '' : '';

  return new Candidate().deserialize({
    Id: item.candidateId,
    CandidateId: item.candidateId,
    FirstName: firstName,
    LastName: lastName,
    DisplayName: displayName,
    Photo: item.media?.imageUrl || null,
    ProfileImageURL: item.media?.imageUrl || null,
    Area: item.location?.area || null,
    Profiles: mapProfiles(item),
    Tags: mapTags(item.tags),
    Score: item.metrics?.consultantScore ?? null,
    Jobs: item.metrics?.completedJobs ?? null,
    JobsAtClient: item.metrics?.completedJobsAtClient ?? null,
    TotalDays: item.metrics?.completedJobs ?? null,
    TotalDaysAtClient: item.metrics?.completedJobsAtClient ?? null,
    DefaultSummary: item.metrics?.defaultSummary || null,
    IsBankStaff: item.flags?.isBankStaff ?? false,
    InTalentPool: item.flags?.isInTalentPool ?? false
  });
}

export function getCandidateSearchDisplayName(name: Pick<CandidateSearchName, 'firstName' | 'goesBy' | 'lastName' | 'hasWorkedForClient'>): string {
  const preferredName = name?.goesBy || name?.firstName || '';
  const lastName = name?.hasWorkedForClient ? name?.lastName || '' : '';

  return [preferredName, lastName].filter(Boolean).join(' ');
}

function mapProfiles(item: CandidateSearchResultItem): CandidateProfile[] {
  const profiles = (item.profiles ?? []).map(profile => new CandidateProfile().deserialize({
    Id: profile.candidateCardId,
    CandidateId: item.candidateId,
    IsPrimary: profile.isPrimary ? 1 : 0,
    Longterm: profile.longTerm,
    Role: profile.roleName,
    RoleId: profile.roleId,
    RoleGroupId: profile.roleGroupId,
    Summary: profile.summary || null,
    Supply: profile.dailySupply,
    DayPayRate: profile.dayPayRate ?? null,
    HalfDayPayRate: profile.halfDayPayRate ?? null,
    LongTermDayPayRate: profile.longTermDayPayRate ?? null,
    HourlyRate: profile.hourlyRate ?? null,
    Tags: []
  }));

  return profiles.length ? profiles : [new CandidateProfile()];
}

function mapTags(tags: CandidateSearchTag[]): Tag[] {
  return (tags ?? []).map(tag => new Tag().deserialize({
    Id: tag.id,
    Name: tag.name
  }));
}
